/**
 * Launch an Rscript as an AWS Batch job via conchBatch.
 *
 * The endpoint runs arbitrary reviewed R in production, so it requires a
 * superadmin and a Bearer token — unlike the older uploadSubmitJob routes,
 * which are unauthenticated. Callers must gate the control on
 * `hasSuperAdminRole` as well; this is the second line, not the first.
 */

type LaunchResponse = { job_id: string; script_path: string; args: string[] };

type LaunchOptions = {
  jobDefinition?: conch.JobDefinition;
  /**
   * Selects the compute environment. Omitted, conchBatch falls back to
   * gg-queue-efs-4xlarge, which is half the cores a training run asks for.
   */
  jobQueue?: string;
};

export default async function httpLaunchRscript(
  scriptPath: string,
  args: string[],
  token: string,
  { jobDefinition, jobQueue }: LaunchOptions = {},
): Promise<LaunchResponse> {
  const response = await fetch('/conchBatch/launchRscript', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    // conchBatch falls back to its own GG_BATCH_JOB_DEFINITION, which is not
    // set on the deployed service, so the environment's definition is sent the
    // same way submitConchBatch already sends `jobdef`. gg-report-* runs the
    // GastrographPackage image with EFS mounted at /home/rstudio/output, which
    // is where the R scripts default their artifact roots.
    body: JSON.stringify({
      script_path: scriptPath,
      args,
      ...(jobDefinition ? { job_definition: jobDefinition } : {}),
      ...(jobQueue ? { job_queue: jobQueue } : {}),
    }),
  });

  // The route ships separately from this console, so an environment still
  // running an older conchBatch answers with its HTML 404 page rather than
  // { error }. Reporting that as a bare status code sends someone hunting for
  // a job that was never submitted.
  if (response.status === 404) {
    throw new Error(
      'Launching Rscripts is not available in this environment yet — conchBatch has no /launchRscript route deployed.',
    );
  }

  // conchBatch reports 400/401/403/502 as { error }. Surfacing that text
  // matters: "requires --job_id or --universe" and "only a SUPERADMIN may
  // launch" need completely different reactions from the person reading it.
  if (!response.ok) {
    const message = await response
      .json()
      .then((body) => body?.error)
      .catch(() => null);
    throw new Error(message || `Request failed (${response.status})`);
  }

  return response.json();
}
